import React from "react";
import {
    Box,
    Card,
    CardHeader,
    Text,
    Heading,
    HStack,
    VStack,
    SimpleGrid,
    Badge,
    Table,
    TableContainer,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Divider,
} from "@chakra-ui/react";
import moment from "moment";
import { COLORS } from "../styles/colors";

/** Drop trailing " — {runId}" from titles; run id is shown under Run ID. */
function programmeDisplayTitle(courseSummary, runLabel, runCode) {
    const runId = (courseSummary && courseSummary.subtitle) || runCode || "";
    const raw = (courseSummary && courseSummary.title) || runLabel || "";
    if (!runId || !raw) return raw;
    const trimmed = raw.trimEnd();
    const suffixes = [` — ${runId}`, ` – ${runId}`, ` - ${runId}`];
    for (const s of suffixes) {
        if (trimmed.endsWith(s)) return trimmed.slice(0, -s.length).trimEnd();
    }
    return raw;
}

function MetaItem({ label, children }) {
    return (
        <Box minW={0}>
            <Text
                fontSize="xs"
                fontWeight="semibold"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="0.08em"
                mb={1.5}
            >
                {label}
            </Text>
            <Box>{children}</Box>
        </Box>
    );
}

const SESSION_CELL_PX = { base: 3, md: 4 };
const SESSION_CELL_PY = 3.5;

/**
 * Full-width session schedule table for corporate enrol run summary.
 * Uses explicit column fractions so Venue/Room stay balanced; scrollbar-gutter avoids header/body drift when scrolling.
 */
export function SessionsScheduleTable({ title, sessions }) {
    if (!sessions?.length) return null;
    return (
        <Box w="100%">
            <Text fontWeight="700" fontSize="md" color={COLORS.maastrichtBlue} mb={3} letterSpacing="-0.01em">
                {title}
            </Text>
            <TableContainer
                w="100%"
                maxH="min(360px, 55vh)"
                overflowY="auto"
                overflowX="auto"
                borderWidth="1px"
                borderRadius="xl"
                borderColor="gray.200"
                bg="white"
                boxShadow="sm"
                sx={{
                    scrollbarGutter: "stable",
                }}
            >
                <Table
                    variant="simple"
                    size="sm"
                    w="100%"
                    minW="min(100%, 560px)"
                    sx={{ tableLayout: "fixed" }}
                >
                    <colgroup>
                        <col style={{ width: "28%" }} />
                        <col style={{ width: "24%" }} />
                        <col style={{ width: "24%" }} />
                        <col style={{ width: "24%" }} />
                    </colgroup>
                    <Thead
                        bg="gray.50"
                        position="sticky"
                        top={0}
                        zIndex={1}
                        sx={{
                            boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.08)",
                        }}
                    >
                        <Tr>
                            <Th
                                px={SESSION_CELL_PX}
                                py={3}
                                fontSize="10px"
                                fontWeight="700"
                                color="gray.600"
                                textTransform="uppercase"
                                letterSpacing="0.06em"
                                borderColor="gray.200"
                                borderBottomWidth="1px"
                            >
                                Date
                            </Th>
                            <Th
                                px={SESSION_CELL_PX}
                                py={3}
                                fontSize="10px"
                                fontWeight="700"
                                color="gray.600"
                                textTransform="uppercase"
                                letterSpacing="0.06em"
                                borderColor="gray.200"
                                borderBottomWidth="1px"
                            >
                                Time
                            </Th>
                            <Th
                                px={SESSION_CELL_PX}
                                py={3}
                                fontSize="10px"
                                fontWeight="700"
                                color="gray.600"
                                textTransform="uppercase"
                                letterSpacing="0.06em"
                                borderColor="gray.200"
                                borderBottomWidth="1px"
                            >
                                Venue
                            </Th>
                            <Th
                                px={SESSION_CELL_PX}
                                py={3}
                                fontSize="10px"
                                fontWeight="700"
                                color="gray.600"
                                textTransform="uppercase"
                                letterSpacing="0.06em"
                                borderColor="gray.200"
                                borderBottomWidth="1px"
                            >
                                Room
                            </Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {sessions.map((s, idx) => (
                            <Tr key={`${s.date}-${idx}`} _hover={{ bg: "gray.50" }} transition="background 0.15s ease">
                                <Td
                                    verticalAlign="middle"
                                    py={SESSION_CELL_PY}
                                    px={SESSION_CELL_PX}
                                    fontSize="sm"
                                    fontWeight="500"
                                    color="gray.800"
                                    borderColor="gray.100"
                                    wordBreak="break-word"
                                    lineHeight="1.45"
                                >
                                    {s.date ? moment(s.date).format("ddd, D MMM YYYY") : "—"}
                                </Td>
                                <Td
                                    verticalAlign="middle"
                                    py={SESSION_CELL_PY}
                                    px={SESSION_CELL_PX}
                                    fontSize="sm"
                                    color="gray.700"
                                    borderColor="gray.100"
                                    whiteSpace="nowrap"
                                    lineHeight="1.45"
                                >
                                    {s.start_time && s.end_time
                                        ? `${moment(s.start_time, "HHmm").format("h:mm A")} – ${moment(
                                              s.end_time,
                                              "HHmm",
                                          ).format("h:mm A")}`
                                        : "—"}
                                </Td>
                                <Td
                                    verticalAlign="middle"
                                    py={SESSION_CELL_PY}
                                    px={SESSION_CELL_PX}
                                    fontSize="sm"
                                    color="gray.700"
                                    borderColor="gray.100"
                                    wordBreak="break-word"
                                    lineHeight="1.45"
                                >
                                    {s.venue || "—"}
                                </Td>
                                <Td
                                    verticalAlign="middle"
                                    py={SESSION_CELL_PY}
                                    px={SESSION_CELL_PX}
                                    fontSize="sm"
                                    color="gray.700"
                                    borderColor="gray.100"
                                    wordBreak="break-word"
                                    lineHeight="1.45"
                                >
                                    {s.room || "—"}
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </TableContainer>
        </Box>
    );
}

/**
 * Course / cert run summary card: portal context, programme title, labelled metadata, sessions.
 */
export function CorporateEnrollRunSummaryCard({
    portalTitle,
    clientCompanyName,
    clientCompanyUen,
    courseSummary,
    runLabel,
    runCode,
    moduleSessions,
    sessionsFlat,
}) {
    const runIdDisplay = (courseSummary && courseSummary.subtitle) || runCode || "";

    const hasSchedule =
        courseSummary && (courseSummary.start_date || courseSummary.end_date);
    const dateRangeText =
        hasSchedule &&
        [
            courseSummary.start_date ? moment(courseSummary.start_date).format("D MMM YYYY") : null,
            courseSummary.end_date ? moment(courseSummary.end_date).format("D MMM YYYY") : null,
        ]
            .filter(Boolean)
            .join(" – ");

    return (
        <Card shadow="lg" borderWidth="1px" borderColor="blackAlpha.100" overflow="hidden" mb={8}>
            <CardHeader
                bg={COLORS.isabelline}
                borderBottomWidth="1px"
                borderColor="blackAlpha.100"
                px={{ base: 4, md: 6 }}
                py={{ base: 5, md: 7 }}
            >
                <VStack align="stretch" spacing={6}>
                    <Box>
                        <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color={COLORS.shadowBlue}
                            textTransform="uppercase"
                            letterSpacing="wider"
                        >
                            {portalTitle}
                        </Text>
                        {clientCompanyName ? (
                            <Heading
                                as="h2"
                                size="sm"
                                mt={3}
                                mb={0}
                                color={COLORS.maastrichtBlue}
                                fontWeight="extrabold"
                                letterSpacing="tight"
                                lineHeight="short"
                                fontSize={{ base: "lg", md: "xl" }}
                            >
                                {clientCompanyName}
                                {clientCompanyUen ? (
                                    <Text
                                        as="span"
                                        fontWeight="semibold"
                                        color="gray.600"
                                        fontSize={{ base: "0.85em", md: "0.82em" }}
                                        ml={1}
                                    >
                                        ({clientCompanyUen})
                                    </Text>
                                ) : null}
                            </Heading>
                        ) : null}
                    </Box>

                    <Heading
                        as="h1"
                        size="md"
                        color={COLORS.maastrichtBlue}
                        lineHeight="short"
                        fontWeight="600"
                    >
                        {programmeDisplayTitle(courseSummary, runLabel, runCode)}
                    </Heading>

                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={{ md: 10 }} spacingY={5} w="100%">
                        <MetaItem label="Run ID">
                            <Text fontWeight="semibold" color="gray.800" fontSize="md">
                                {runIdDisplay || "—"}
                            </Text>
                        </MetaItem>
                        <MetaItem label="Programme type">
                            <HStack flexWrap="wrap" gap={2}>
                                {courseSummary?.kind === "full_cert_run" ? (
                                    <Badge colorScheme="purple" px={2.5} py={1} borderRadius="md" fontSize="0.75rem">
                                        Certification intake
                                    </Badge>
                                ) : (
                                    <Badge colorScheme="blue" px={2.5} py={1} borderRadius="md" fontSize="0.75rem">
                                        Course run
                                    </Badge>
                                )}
                            </HStack>
                        </MetaItem>
                    </SimpleGrid>

                    {(dateRangeText || courseSummary?.venue) && (
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={{ md: 10 }} spacingY={5} w="100%">
                            {dateRangeText ? (
                                <MetaItem label="Schedule">
                                    <Text color="gray.700" fontSize="sm" fontWeight="medium">
                                        {dateRangeText}
                                    </Text>
                                </MetaItem>
                            ) : null}
                            {courseSummary?.venue ? (
                                <MetaItem label="Venue">
                                    <Text color="gray.700" fontSize="sm">
                                        {courseSummary.venue}
                                    </Text>
                                </MetaItem>
                            ) : null}
                        </SimpleGrid>
                    )}

                    {courseSummary?.description ? (
                        <Box>
                            <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="0.08em"
                                mb={2}
                            >
                                About this programme
                            </Text>
                            <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap" lineHeight="tall">
                                {courseSummary.description}
                            </Text>
                        </Box>
                    ) : null}

                    <Divider borderColor="blackAlpha.150" />

                    {moduleSessions.length > 0 ? (
                        <VStack align="stretch" spacing={8} w="100%">
                            {moduleSessions.map((m) => (
                                <SessionsScheduleTable key={m.label} title={m.label} sessions={m.sessions} />
                            ))}
                        </VStack>
                    ) : (
                        <SessionsScheduleTable title="Sessions" sessions={sessionsFlat} />
                    )}
                </VStack>
            </CardHeader>
        </Card>
    );
}
